import { Loader } from '@mantine/core';
import { Check, Warning } from '@phosphor-icons/react';
import { type FC } from 'react';

import styles from './SaveState.module.css';

export type SaveStatus = 'idle' | 'saving' | 'saved' | 'failed';

export interface SaveStateProps {
    status: SaveStatus;
    /** Shown in place of the generic label when the save failed: the server's reason. */
    message?: string | null;
    /** What to say while idle. Empty renders nothing in that state. */
    idleLabel?: string;
}

/**
 * The small "saving / saved / could not save" word beside a field that writes on blur.
 *
 * Five screens hand-rolled their own before this existed. One component means a failed save
 * reads the same wherever it happens, and always carries the server's reason rather than a
 * generic "try again".
 */
const SaveState: FC<SaveStateProps> = ({ status, message, idleLabel = '' }) => {
    if (status === 'idle') {
        return idleLabel ? <span className={`${styles.state} ${styles.idle}`}>{idleLabel}</span> : null;
    }
    if (status === 'saving') {
        return (
            <span className={`${styles.state} ${styles.saving}`} role="status">
                <Loader size={10} color="gray" /> Saving…
            </span>
        );
    }
    if (status === 'saved') {
        return (
            <span className={`${styles.state} ${styles.saved}`} role="status">
                <Check size={12} weight="bold" /> Saved
            </span>
        );
    }
    return (
        <span className={`${styles.state} ${styles.failed}`} role="alert">
            <Warning size={12} weight="bold" /> {message || 'Could not save'}
        </span>
    );
};

export default SaveState;
